import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@material-ui/core";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useShiftPageStyles } from "../styles";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { InfoBox } from "componentsNew/elements/InfoBox";
import { useShiftReportDetails } from "hooks/useShiftReportDetails";
import { getTimeSincePost } from "utils/filters";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import {
  Add,
  DownloadOutlined as DownloadOutlinedIcon,
  EditOutlined,
  GroupAddOutlined as GroupAddOutlinedIcon,
  GroupRemoveOutlined as GroupRemoveOutlinedIcon,
  KeyboardArrowDown,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowUp,
  NotificationsOutlined as NotificationsOutlinedIcon,
  PublishOutlined as PublishOutlinedIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { ModalWindow } from "componentsNew/modals/ModalWindow";
import {
  EditWavesModalContent,
  EnableSecondDriverModal,
  SendDriversMessageModal,
  UploadExeShiftDataModal,
} from "componentsNew/modals/modalContent/ShiftModelContent";
import { useDispatch } from "react-redux";
import { openModal } from "actions/modalActions";
import { useNav } from "hooks/useNav";
import { createReportRecordAction, shiftRecordsUpdateFilters } from "actions/shiftTrackerActions";
import { SearchInput } from "componentsNew/elements/SearchInput";
import { CustomDrawer } from "componentsNew/drawers/CustomDrawer";
import { ShiftTrackerTableSettingsDrawerContent } from "componentsNew/drawers/drawerContent/ShiftTrackerTableSettingsDrawerContent";
import { PdfGenerationModal } from "componentsNew/modals/PdfGenerationModal";

interface ActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

const ShiftRecordActionsMenu: FC<{
  unpublishedMessages: number;
  hasSecondDriver: boolean;
  disabled: boolean;
  onAddRecord: () => void;
  onOpenModal: (modalName: string) => void;
}> = ({ unpublishedMessages, hasSecondDriver, disabled, onAddRecord, onOpenModal }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => setAnchorEl(null);

  const handleOpenModal = (modalName: string) => {
    handleClose();
    onOpenModal(modalName);
  };

  const handleAddRecord = () => {
    handleClose();
    onAddRecord();
  };

  const actionItems: ActionItem[] = [
    {
      key: "notify",
      label: `Notify (${unpublishedMessages})`,
      icon: <NotificationsOutlinedIcon fontSize="small" />,
      onClick: () => handleOpenModal("whats-up-notify-modal"),
    },
    {
      key: "add-record",
      label: "Add Record",
      icon: <Add fontSize="small" />,
      onClick: handleAddRecord,
    },
    {
      key: "edit-waves",
      label: "Edit Waves",
      icon: <EditOutlined fontSize="small" />,
      onClick: () => handleOpenModal("wave-edit-modal"),
    },
    {
      key: "helper",
      label: hasSecondDriver ? "Hide Helper" : "Add Helper",
      icon: hasSecondDriver ? (
        <GroupRemoveOutlinedIcon fontSize="small" />
      ) : (
        <GroupAddOutlinedIcon fontSize="small" />
      ),
      onClick: () => handleOpenModal("enable-second-driver-model"),
    },
    {
      key: "upload-report",
      label: "Upload Report",
      icon: <PublishOutlinedIcon fontSize="small" />,
      onClick: () => handleOpenModal("upload-file-modal"),
    },
    {
      key: "download-pdf",
      label: "Download PDF",
      icon: <DownloadOutlinedIcon fontSize="small" />,
      onClick: () => handleOpenModal("download-pdf-modal"),
    },
  ];

  return (
    <>
      <CustomButton
        buttonText={"Actions"}
        onClick={(event: any) => setAnchorEl(event?.currentTarget)}
        variant="contained"
        icon={anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        disabled={disabled}
        swapPositions
      />
      <Menu
        id="shift-record-actions-menu"
        anchorEl={anchorEl}
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {actionItems.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ShiftRecordHeadModals: FC<{
  hasSecondDriver: boolean;
  disabled: boolean;
}> = ({ hasSecondDriver, disabled }) => (
  <>
    <ModalWindow
      buttonType={"none"}
      modal_name={"no-vans-modal"}
      modalTitle={"No Free Vans"}
      modalDescription={`
          There are no free vans for current driver with such fleet size. 
          Try select this driver again with another fleet size or connect
          with administrator!
        `}
      children={<></>}
    />
    <ModalWindow modal_name={"wave-edit-modal"} buttonType={"none"} modalTitle={"Edit Waves"}>
      <EditWavesModalContent />
    </ModalWindow>
    <ModalWindow
      buttonType={"none"}
      fullWidth
      modal_name={"enable-second-driver-model"}
      modalTitle={hasSecondDriver ? "Hide Helper" : "Add Helper"}
      modalDescription={
        hasSecondDriver
          ? "Hide second driver from the table?"
          : "Add second driver to the table?"
      }
      isDisabled={disabled}
    >
      <EnableSecondDriverModal />
    </ModalWindow>
    <ModalWindow
      buttonType={"none"}
      modal_name={"whats-up-notify-modal"}
      modalTitle={"Send notification"}
      isDisabled={disabled}
    >
      <SendDriversMessageModal />
    </ModalWindow>
    <ModalWindow
      buttonType={"none"}
      modal_name={"upload-file-modal"}
      modalTitle={"Upload Report (.xlsx)"}
      modalDescription={
        "Data processing should take around 15 seconds after the file is uploaded. After this stage, reload the page to see the updated rows."
      }
      isDisabled={disabled}
    >
      <UploadExeShiftDataModal />
    </ModalWindow>
    <PdfGenerationModal />
  </>
);

const ShiftPageHead: FC = () => {
  const dispatch = useDispatch();
  const { viewReports } = useNav();
  const { id } = useNav().pageParams;
  const classes = useShiftPageStyles();
  const { userData } = useTypeSelectors((state) => state.auth);
  const { currentReport, currentReportDate, recordListFilters, hasSecondDriver } = useTypeSelectors(
    (state) => state.dasBoard,
  );
  const [disabledByDate, setDisabledByDate] = useState(getTimeSincePost(currentReportDate));
  const conditionForDisabledEdit = userData?.can_create_old_shifts ? false : disabledByDate;
  const { overview } = useShiftReportDetails();

  useEffect(() => {
    if (currentReportDate) {
      setDisabledByDate(getTimeSincePost(currentReportDate));
    }
  }, [currentReportDate]);

  const handleOpenModal = (modalName: string) => {
    dispatch(openModal(modalName));
  };

  const handleAddRecord = () => {
    dispatch(createReportRecordAction(id));
  };

  const handleSearch = useCallback(
    (search: string) => {
      if ((recordListFilters.search || "") === search) return;
      dispatch(shiftRecordsUpdateFilters({ search }));
    },
    [dispatch, recordListFilters.search],
  );

  const unpublishedMessages =
    currentReport.filter((record) => !record.is_status_published && record.driver).length +
    currentReport.filter((record) => !record.is_second_status_published && record.second_driver)
      .length;

  return (
    <Box className={classes.paper}>
      <Box className={classes.topSection}>
        <Box className={classes.sectionLeft}>
          <CustomButton
            buttonText={"Back"}
            onClick={() => viewReports()}
            variant="text"
            icon={<KeyboardArrowLeftIcon />}
          />
        </Box>
        <Box className={classes.sectionRight}>
          <Box style={{ display: "flex", gap: 8 }}>
            <SearchInput
              value={recordListFilters.search || ""}
              onChange={handleSearch}
              placeholder={"Delivery Associate / Role / Fleet Vendor / Fleet No."}
              isWide
            />
            <CustomDrawer
              customWidth={440}
              buttonType={"custom"}
              drawer_name={"shift-page-table-settings-drawer"}
              drawerTitle={"Table Settings"}
              customButtonElement={
                <CustomButton
                  buttonText={"Settings"}
                  onClick={() => null}
                  variant="outlined"
                  icon={<TuneIcon />}
                />
              }
            >
              <ShiftTrackerTableSettingsDrawerContent />
            </CustomDrawer>
          </Box>
          <ShiftRecordActionsMenu
            unpublishedMessages={unpublishedMessages}
            hasSecondDriver={hasSecondDriver}
            disabled={conditionForDisabledEdit}
            onAddRecord={handleAddRecord}
            onOpenModal={handleOpenModal}
          />
        </Box>
      </Box>
      <Box className={classes.bottomSection}>
        {overview.map((item) => (
          <InfoBox key={item.label} label={item.label} value={item.value} />
        ))}
      </Box>
      <ShiftRecordHeadModals
        hasSecondDriver={hasSecondDriver}
        disabled={conditionForDisabledEdit}
      />
    </Box>
  );
};

export default ShiftPageHead;
